import type {
  BlogCategoriesParams,
  BlogCategoriesResponse,
  BlogFrontPageParams,
  BlogFrontPageResponse,
  BlogPostParams,
  BlogPostResponse,
  BlogPostsParams,
  BlogPostsResponse,
  CollectionParams,
  CollectionResponse,
  CollectionsParams,
  CollectionsResponse,
  FooterLayoutParams,
  FooterLayoutResponse,
  NavigationLayoutParams,
  NavigationLayoutResponse,
  PageParams,
  PageResponse,
  ProductPageConfigParams,
  ProductPageConfigResponse
} from './storyblok.types';

import { removeLocaleFromPath } from '../utils/locale.shared';

const API_PREFIX = 'api'; // PUBLIC_API === 'v2' ? 'apiv2' : 'api';

const DEFAULT_PER_PAGE = 24;

const getQueryParams = (params: Record<string, string | number | undefined>) => {
  return Object.entries(params)
    .filter(([_key, value]) => value)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
}

// Fetch blog front page data
export const getBlogFrontPage = async (fetch: typeof globalThis.fetch, params: BlogFrontPageParams): Promise<BlogFrontPageResponse> => {
  const { locale, origin = '' } = params;
  console.log('storyblok.client:getBlogFrontPage', locale);

  const searchParams = getQueryParams({ locale });
  console.log('storyblok.client:getBlogFrontPage', `${origin}/${API_PREFIX}/blog/front-page?${searchParams}`);
  const startTime = performance.now();
  const response = await fetch(`${origin}/${API_PREFIX}/blog/front-page?${searchParams}`);
  const endTime = performance.now();
  console.log('storyblok.client:getBlogFrontPage.age', response.headers.get('age'));
  console.log(`storyblok.client:getBlogFrontPage.took ${endTime - startTime}ms`);
  return response.json();
}

// Fetch blog posts
export const getBlogPosts = async (fetch: typeof globalThis.fetch, params: BlogPostsParams): Promise<BlogPostsResponse> => {
  const { locale, page, categoryId, perPage = DEFAULT_PER_PAGE, origin = '', slugs, category } = params;
  console.log('storyblok.client:getBlogPosts', locale, page, perPage, categoryId, slugs, category);

  const searchParams = getQueryParams({ locale, page, perPage, categoryId, slugs, category });
  console.log('storyblok.client:getBlogPosts', `${origin}/${API_PREFIX}/blog/posts?${searchParams}`);
  const startTime = performance.now();
  const response = await fetch(`${origin}/${API_PREFIX}/blog/posts?${searchParams}`);
  const endTime = performance.now();
  console.log('storyblok.client:getBlogPosts.age', response.headers.get('age'));
  console.log(`storyblok.client:getBlogPosts.took ${endTime - startTime}ms`);
  return response.json();
}

export const getBlogPost = async (fetch: typeof globalThis.fetch, params: BlogPostParams): Promise<BlogPostResponse | null> => {
  const { locale, slug, origin = '' } = params;
  console.log('storyblok.client:getBlogPost', locale, slug);

  const searchParams = getQueryParams({ locale, slug });
  console.log('storyblok.client:getBlogPost', `${origin}/${API_PREFIX}/blog/post?${searchParams}`);
  const startTime = performance.now();
  const response = await fetch(`${origin}/${API_PREFIX}/blog/post?${searchParams}`);
  const endTime = performance.now();
  console.log('storyblok.client:getBlogPost.age', response.headers.get('age'));
  console.log(`storyblok.client:getBlogPost.took ${endTime - startTime}ms`);

  if (response.ok) {
    return response.json();
  }
  return null;
}

export const getBlogCategories = async (fetch: typeof globalThis.fetch, params: BlogCategoriesParams): Promise<BlogCategoriesResponse> => {
  const { locale, slug, origin = '' } = params;
  console.log('storyblok.client:getBlogCategories', locale);

  const searchParams = getQueryParams({ locale, slug });
  console.log('storyblok.client:getBlogCategories', `${origin}/${API_PREFIX}/blog/category?${searchParams}`);
  const startTime = performance.now();
  const response = await fetch(`${origin}/${API_PREFIX}/blog/category?${searchParams}`);
  const endTime = performance.now();
  console.log('storyblok.client:getBlogCategories.age', response.headers.get('age'));
  console.log(`storyblok.client:getBlogCategories.took ${endTime - startTime}ms`);
  return response.json();
}

export const getCollections = async (fetch: typeof globalThis.fetch, params: CollectionsParams): Promise<CollectionsResponse> => {
  const { locale, type, origin = '' } = params;
  console.log('storyblok.client:getCollections', locale, type);

  const searchParams = getQueryParams({ locale, type });
  console.log('storyblok.client.getCollections', `${origin}/${API_PREFIX}/collections?${searchParams}`);
  const startTime = performance.now();
  const response = await fetch(`${origin}/${API_PREFIX}/collections?${searchParams}`);
  const endTime = performance.now();
  console.log('storyblok.client.getCollections.age', response.headers.get('age'));
  console.log(`storyblok.client.getCollections.took ${endTime - startTime}ms`);
  return response.json();
}

export const getCollection = async (fetch: typeof globalThis.fetch, params: CollectionParams): Promise<CollectionResponse | null> => {
  const { locale, url, origin = '' } = params;
  const fullSlug = encodeURIComponent(removeLocaleFromPath(url.pathname));
  console.log('storyblok.client:getCollection', locale, fullSlug);

  const searchParams = getQueryParams({ full_slug: fullSlug, locale: locale });
  console.log('storyblok.client.getCollection', `${origin}/${API_PREFIX}/collection?${searchParams}`);
  const startTime = performance.now();
  const categoryResponse = await fetch(`${origin}/${API_PREFIX}/collection?${searchParams}`);
  const endTime = performance.now();
  console.log('storyblok.client.getCollection.age', categoryResponse.headers.get('age'));
  console.log(`storyblok.client.getCollection.took ${endTime - startTime}ms`);
  if (categoryResponse.ok) {
    return categoryResponse.json();
  }
  return null;
}

export const getProductPageConfig = async (fetch: typeof globalThis.fetch, params: ProductPageConfigParams): Promise<ProductPageConfigResponse> => {
  const { locale, market, origin = '' } = params;
  console.log('storyblok.client:getProductPageConfig', locale, market);

  const searchParams = getQueryParams({ locale, market });
  console.log('storyblok.client:getProductPageConfig', `${origin}/${API_PREFIX}/product/config?${searchParams}`);
  const startTime = performance.now();
  const response = await fetch(`${origin}/${API_PREFIX}/product/config?${searchParams}`);
  const endTime = performance.now();
  console.log('storyblok.client:getProductPageConfig.age', response.headers.get('age'));
  console.log(`storyblok.client:getProductPageConfig.took ${endTime - startTime}ms`);
  return response.json();
}

/**
* Get navigation data from API (content-api)
*/
export const getNavigationLayout = async (fetch: typeof globalThis.fetch, params: NavigationLayoutParams): Promise<NavigationLayoutResponse> => {
  const { locale, market, origin = '' } = params;
  console.log('storyblok.client.getNavigationData', locale, market);

  const searchParams = getQueryParams({ locale, market });
  console.log('storyblok.client.getNavigationData', `${origin}/${API_PREFIX}/layout/nav?${searchParams}`);
  const startTime = performance.now();
  const response = await fetch(`${origin}/${API_PREFIX}/layout/nav?${searchParams}`);
  const endTime = performance.now();
  console.log('storyblok.client.getNavigationData.age', response.headers.get('age'));
  console.log(`storyblok.client.getNavigationData.took ${endTime - startTime}ms`);
  return await response.json();
};

/**
 * Get footer data from API (content-api)
 */
export const getFooterLayout = async (fetch: typeof globalThis.fetch, params: FooterLayoutParams): Promise<FooterLayoutResponse> => {
  const { locale, market, origin = '' } = params;
  console.log('storyblok.client.getFooterData', locale, market);

  const searchParams = getQueryParams({ locale, market });
  console.log('storyblok.client.getFooterData', `${origin}/${API_PREFIX}/layout/footer?${searchParams}`);
  const startTime = performance.now();
  const response = await fetch(`${origin}/${API_PREFIX}/layout/footer?${searchParams}`);
  const endTime = performance.now();
  console.log('storyblok.client.getFooterData.age', response.headers.get('age'));
  console.log(`storyblok.client.getFooterData.took ${endTime - startTime}ms`);
  return await response.json();
};

export const getPage = async (fetch: typeof globalThis.fetch, params: PageParams): Promise<PageResponse | null> => {
  const { locale, market, slug, fetchFromRoot = false, origin = '' } = params;
  console.log('storyblok.client:getPage', locale, market, slug, fetchFromRoot);

  const searchParams = getQueryParams({ locale, market, slug, fetchFromRoot: fetchFromRoot ? 'true' : undefined });
  console.log('storyblok.client:getPage', `${origin}/${API_PREFIX}/pages?${searchParams}`);
  const startTime = performance.now();
  const response = await fetch(`${origin}/${API_PREFIX}/pages?${searchParams}`);
  const endTime = performance.now();
  console.log('storyblok.client:getPage.age', response.headers.get('age'));
  console.log(`storyblok.client:getPage.took ${endTime - startTime}ms`);

  if (response.ok) {
    return await response.json();
  }

  try {
    console.error(response.status, { message: await response.json() });
  } catch (error) {
    console.error(response.status, response.statusText, response.url.toString());
  }

  return null;
}
